import { document } from "@packages/global";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ContextMenuButton } from "./context-menu-button.component";
import { ContextMenuContent } from "./context-menu-content.component";
import { ContextMenuItem } from "./context-menu-item.component";
import { useContextMenu } from "./hooks";
import { ContextMenuProvider } from "./providers";

export type ContextMenuProps = {
  children: ReactNode;
  variant?: "primary" | "secondary";
} & React.AllHTMLAttributes<HTMLDivElement>;

export function _ContextMenu({
  children,
  variant = "primary",
  ...props
}: ContextMenuProps) {
  const { setIsOpen, isOpen, setComponentVariant } = useContextMenu();
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [dataQaDevice, setDataQaDevice] = useState('');

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    },
    [setIsOpen]
  );

  useEffect(() => {
    setComponentVariant(variant);
  }, [setComponentVariant, variant]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  function handleMouseEnter() {
    setIsOpen(true);
  }

  function handleMouseLeave() {
    setIsOpen(false);
  }

  function handleClick() {
    setIsOpen((prevState: boolean) => !prevState);
  }

  useEffect(() => {
    if (window.innerWidth < 842) {
      setDataQaDevice('-mobile')
    }
  }, [])

  return (
    <div
      ref={contextMenuRef}
      onMouseLeave={variant === "primary" ? handleMouseLeave : undefined}
      onMouseEnter={variant === "primary" ? handleMouseEnter : undefined}
      onClick={variant === "secondary" ? handleClick : undefined}
      style={{ position: "relative" }}
      data-qa={`order_by${dataQaDevice}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function ContextMenu({ children, ...props }: ContextMenuProps) {
  return (
    <ContextMenuProvider>
      <_ContextMenu {...props}>{children}</_ContextMenu>
    </ContextMenuProvider>
  );
}

export namespace ContextMenu {
  export const Content = ContextMenuContent;
  export const Button = ContextMenuButton;
  export const Item = ContextMenuItem;
}
